import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  useAppsLoader,
  useArticlesLoader,
  useAutomationsLoader,
  useCategoriesLoader,
  useDataLinksLoader,
  useMenuItemsLoader,
  usePagesLoader,
  usePageTemplatesLoader,
  usePromptLoader,
  useAuthorisedRolesLoader,
  useSettingsLoader,
  //useStatsLoader,
  useTemplateLoader,
  useUserStatsLoader
} from './loaders';

import {
  dataRequestedAction,
  dataProcessingAction,
  STORE_ACTIONS
} from '../actions/store';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { consoleLog } from '../../utils/Logger';

/*
  Use this hook instead of useSelector

  It either returns data from the store or requests that the useFirestoreHook load the 
    data from firestore using a designated data loader hook
*/
const useStoreSelector = (
  selector,
  options = { reload: false, source: '', ignore: false }
) => {
  const dispatch = useDispatch();

  const [loaded, setLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { snapshots } = useSelector(state => state.Store);
  const state = useSelector(selector);

  // in a minified environment the selector "state" will not be named state
  let storePath = selector
    .toString()
    .substring(selector.toString().indexOf('=>'));
  const startIndex = storePath.indexOf('.') + 1;
  storePath = storePath.substring(startIndex).split('.');

  const slice = storePath[0];
  const path = storePath.length > 1 ? storePath[1] : '';

  useEffect(() => {
    consoleLog('useStoreSelector.dataRequestedAction.check', {
      source: options?.source,
      slice,
      path
    });
    if (!options?.ignore) {
      if (
        (options.reload && !loaded) ||
        !snapshots.find(s => s.slice === slice && s.path === path)
      ) {
        setLoaded(true);
        consoleLog(
          'dataRequestedAction',
          { source: options?.source, slice, path },
          snapshots.length
        );
        dispatch(
          dataRequestedAction({ slice, path, source: options?.source || '' })
        );
      }
    }
  }, []);

  useEffect(() => {
    if (
      !dataLoaded &&
      snapshots?.find(
        s =>
          s.slice === slice &&
          s.path === path &&
          s.status === STORE_ACTIONS.DATA_REQUEST_COMPLETED
      )
    ) {
      consoleLog('useStoreSelector.setDataLoaded', {
        source: options?.source,
        slice,
        path
      });
      setDataLoaded(true);
    }
  }, [snapshots]);

  return {
    state,
    loaded: dataLoaded
  };
};

/*
  This hook should be used in a component that will not get unloaded unless the browser is refreshed
  Its purpose is to be a singleton instance within the app to manage the loading of firestore snapshots on demand

  The useStoreSelector should be used within any component. If it cant find the data already loaded in the store it
    dispatches an entry into Store.snapshots with { slice, path, reload: true }

  When useFirestoreManager sees an entry with required: true, it calls the appropriate data loader function
    to populate the store and sets required to false, preventing subsequent requests from triggering multiple snapshot listeners
*/
const useFirestoreManager = () => {
  const { loadApps } = useAppsLoader();
  const { loadArticles } = useArticlesLoader();
  const { loadAutomations } = useAutomationsLoader();
  const { loadCategories } = useCategoriesLoader();
  const { loadDataLinks } = useDataLinksLoader();
  const { loadMenuItems } = useMenuItemsLoader();
  const { loadPages } = usePagesLoader();
  const { loadPageTemplateAndSnippets } = usePageTemplatesLoader();
  const { loadPrompts } = usePromptLoader();
  const { loadAuthorisedRoles } = useAuthorisedRolesLoader();
  const { loadSettings } = useSettingsLoader();
  //const { loadStats } = useStatsLoader();
  const { loadTemplates } = useTemplateLoader();
  const { loadUserStats } = useUserStatsLoader();

  const user = useSelector(state => state.User);
  const { snapshots } = useSelector(state => state.Store);
  const dispatch = useDispatch();
  const [authenticatedUser] = useAuthState(auth);

  /*
    represents know slices of the store along with designated load functions to populate those slices and paths
    A path has the form: { name, function, stream }
      name: the key name in the slice that the load function will store the data in
      stream: if true, denotes that the loadfunction uses a snapshot listener
      function: a loader function from a hook that will load the data directly or instantiate a firestore snapshot listener
  */
  const STORE_SLICES = {
    Sidebar: { name: 'Sidebar', paths: [] },
    Layout: { name: 'Layout', paths: [] },
    Content: { name: 'Content', paths: [] },
    User: {
      name: 'User',
      paths: [
        {
          name: 'userstats',
          function: loadUserStats,
          stream: true
        }
      ]
    },
    Documents: { name: 'Documents', paths: [] },
    Routing: { name: 'Routing', paths: [] },
    Preloaded: {
      name: 'Preloaded',
      paths: [
        {
          name: 'apps',
          function: loadApps,
          stream: true,
          authenticated: true
        },
        {
          name: 'articles',
          function: loadArticles,
          stream: true,
          authenticated: true
        },
        {
          name: 'automations',
          function: loadAutomations,
          stream: true,
          authenticated: true
        },
        /*
        {
          name: 'stats',
          function: loadStats,
          stream: true,
          authenticated: true,
          roles: ['admin', 'owner']
        },*/ {
          name: 'categories',
          function: loadCategories,
          stream: true
        },
        {
          name: 'menuItems',
          function: loadMenuItems,
          stream: true,
          authenticated: true
        },
        {
          name: 'pages',
          function: loadPages,
          stream: true,
          authenticated: true
        },
        {
          name: 'pageTemplates',
          function: loadPageTemplateAndSnippets,
          stream: true,
          authenticated: true
        },
        {
          name: 'prompts',
          function: loadPrompts,
          stream: true,
          authenticated: true
        },
        {
          name: 'authorisedRoles',
          function: loadAuthorisedRoles,
          stream: true
        },
        {
          name: 'settings',
          function: loadSettings,
          stream: true,
          authenticated: true
        },
        {
          name: 'templates',
          function: loadTemplates,
          stream: true,
          authenticated: true
        }
      ]
    },
    AI: {
      name: 'AI',
      paths: []
    },
    DataLinks: {
      name: 'DataLinks',
      paths: [
        {
          name: 'dataLinks',
          function: loadDataLinks,
          stream: true,
          authenticated: true
        }
      ]
    },
    ActionSequences: {
      name: 'ActionSequences',
      paths: []
    },
    Local: { name: 'Local', paths: [] }
  };

  useEffect(() => {
    snapshots
      ?.filter(s => s.status === STORE_ACTIONS.DATA_REQUEST_ISSUED)
      .forEach(snapshot => {
        if (!STORE_SLICES[snapshot.slice]) {
          console.warn('useFirestoreManager: invalid slice', {
            snapshot
          });
          return;
        }

        const dataLoader = STORE_SLICES[snapshot.slice].paths.find(
          p => p.name === snapshot.path
        );
        if (dataLoader) {
          if (
            !dataLoader.authenticated ||
            (dataLoader.authenticated &&
              (user?.validUser || authenticatedUser !== undefined))
          ) {
            if (
              dataLoader.roles?.length > 0 &&
              !dataLoader.roles.some(r => r === user.role)
            ) {
              return;
            }
            dispatch(
              dataProcessingAction({
                slice: snapshot.slice,
                path: snapshot.path
              })
            );
            dataLoader.function();
          }
        }
      });
  }, [snapshots]);
};

export { useStoreSelector, useFirestoreManager };
